import type { DynamicGridItem } from '@cast/design-system';

import { WORKLOAD_CATEGORIES } from '../../_constants';
import type { WorkloadCategory } from '../../_constants';

export enum WorkloadsDashboardItemId {
  CpuUtilization = 'cpu-utilization',
  MemoryUtilization = 'memory-utilization',
  WorkloadAutoscalerCoverage = 'workload-autoscaler-coverage',
  RecommendationStatus = 'recommendation-status',
  ScalingPolicies = 'scaling-policies',
  CpuImpact = 'cpu-impact',
  MemoryImpact = 'memory-impact',
  AutoscalerImpact = 'autoscaler-impact',
  ReclaimedResources = 'reclaimed-resources',
  SavingsSummary = 'savings-summary',
  NetworkCost = 'network-cost',
  NetworkTraffic = 'network-traffic',
  NetworkCostOverTime = 'network-cost-over-time',
  GpuWorkloads = 'gpu-workloads',
  GpuMemoryWaste = 'gpu-memory-waste',
  GpuIdleSpend = 'gpu-idle-spend',
  ReliabilityMap = 'reliability-map',
  WorkloadHealth = 'workload-health',
  WorkloadHealthTrafficShare = 'workload-health-traffic-share',
  P95LatencyDistribution = 'p95-latency-distribution',
  ComputeCost = 'compute-cost',
  BiggestCostMovers = 'biggest-cost-movers',
  ComputeSpend = 'compute-spend',
}

export type WorkloadsDashboardItem = DynamicGridItem & {
  id: WorkloadsDashboardItemId;
};

// Sizing convention:
// - Charts default to half-width (w: 6) and stay readable: minW: 6, minH: 4.
// - Summary cards (e.g. autoscaler impact) can shrink further: minW: 3, minH: 3.
export const WORKLOADS_DASHBOARD_ITEMS: WorkloadsDashboardItem[] = [
  {
    id: WorkloadsDashboardItemId.WorkloadAutoscalerCoverage,
    title: 'Workload Autoscaler coverage',
    description: 'Autoscaler coverage across workloads',
    x: 0,
    y: 0,
    w: 4,
    h: 4,
    minW: 4,
    minH: 4,
  },
  {
    id: WorkloadsDashboardItemId.RecommendationStatus,
    title: 'Recommendation status',
    description: 'Status of applied recommendations',
    x: 4,
    y: 0,
    w: 4,
    h: 4,
    minW: 4,
    minH: 4,
  },
  {
    id: WorkloadsDashboardItemId.AutoscalerImpact,
    title: 'Workload Autoscaler impact',
    description: 'Summary of autoscaler adoption across workloads',
    x: 0,
    y: 4,
    w: 4,
    h: 3,
    minW: 3,
    minH: 3,
  },
  {
    id: WorkloadsDashboardItemId.ReclaimedResources,
    title: 'Reclaimed resources',
    description: 'Average reclaimed CPU and memory resources',
    x: 4,
    y: 4,
    w: 4,
    h: 3,
    minW: 3,
    minH: 3,
  },
  {
    id: WorkloadsDashboardItemId.SavingsSummary,
    title: 'Workload Autoscaler savings',
    description: 'Available and realized monthly cost savings',
    x: 8,
    y: 4,
    w: 4,
    h: 3,
    minW: 3,
    minH: 3,
  },
  {
    id: WorkloadsDashboardItemId.ScalingPolicies,
    title: 'Scaling policies',
    description: 'Distribution of scaling policies',
    x: 0,
    y: 7,
    w: 4,
    h: 3,
    minW: 3,
    minH: 3,
  },
  {
    id: WorkloadsDashboardItemId.CpuUtilization,
    title: 'CPU Utilization',
    description: 'CPU utilization over time',
    x: 0,
    y: 10,
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  {
    id: WorkloadsDashboardItemId.MemoryUtilization,
    title: 'Memory Utilization',
    description: 'Memory utilization over time',
    x: 0,
    y: 14,
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  {
    id: WorkloadsDashboardItemId.CpuImpact,
    title: 'CPU Impact',
    description: 'CPU autoscaling impact',
    x: 0,
    y: 18,
    w: 12,
    h: 5,
    minW: 6,
    minH: 4,
  },
  {
    id: WorkloadsDashboardItemId.MemoryImpact,
    title: 'Memory Impact',
    description: 'Memory autoscaling impact',
    x: 0,
    y: 23,
    w: 12,
    h: 5,
    minW: 6,
    minH: 4,
  },
  {
    id: WorkloadsDashboardItemId.NetworkCost,
    title: 'Network cost',
    description: 'Total network cost for selected workloads',
    x: 0,
    y: 28,
    w: 6,
    h: 3,
    minW: 3,
    minH: 3,
  },
  {
    id: WorkloadsDashboardItemId.NetworkTraffic,
    title: 'Network traffic',
    description: 'Total network traffic for selected workloads',
    x: 6,
    y: 28,
    w: 6,
    h: 3,
    minW: 3,
    minH: 3,
  },
  {
    id: WorkloadsDashboardItemId.NetworkCostOverTime,
    title: 'Network cost',
    description: 'Top workloads by network cost over time',
    x: 0,
    y: 31,
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  {
    id: WorkloadsDashboardItemId.GpuWorkloads,
    title: 'GPU Workloads',
    description: 'Total GPU workloads and memory breakdown',
    x: 0,
    y: 35,
    w: 6,
    h: 3,
    minW: 3,
    minH: 3,
  },
  {
    id: WorkloadsDashboardItemId.GpuMemoryWaste,
    title: 'Total GPU memory waste',
    description: 'Total GPU memory waste for selected workloads',
    x: 6,
    y: 35,
    w: 6,
    h: 3,
    minW: 3,
    minH: 3,
  },
  {
    id: WorkloadsDashboardItemId.GpuIdleSpend,
    title: 'Idle GPU spend',
    description: 'Top workloads by idle GPU spend over time',
    x: 0,
    y: 38,
    w: 12,
    h: 4,
    minW: 6,
    minH: 3,
  },
  {
    id: WorkloadsDashboardItemId.ReliabilityMap,
    title: 'Reliability map',
    description:
      'Workload reliability scatter chart with highest to lowest list',
    x: 0,
    y: 42,
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  {
    id: WorkloadsDashboardItemId.WorkloadHealth,
    title: 'Workloads by health',
    description: 'Distribution of workload health status',
    x: 0,
    y: 46,
    w: 6,
    h: 2,
    minW: 3,
    minH: 2,
  },
  {
    id: WorkloadsDashboardItemId.WorkloadHealthTrafficShare,
    title: 'Traffic distribution per workload health',
    description: 'Traffic share grouped by workload health',
    x: 6,
    y: 46,
    w: 6,
    h: 2,
    minW: 3,
    minH: 2,
  },
  {
    id: WorkloadsDashboardItemId.P95LatencyDistribution,
    title: 'Workload P95 latency distribution',
    description: 'Distribution of workload P95 latency',
    x: 0,
    y: 48,
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  {
    id: WorkloadsDashboardItemId.ComputeCost,
    title: 'Compute cost',
    description: 'Current month compute spend and top spenders',
    x: 0,
    y: 52,
    w: 4,
    h: 4,
    minW: 3,
    minH: 3,
  },
  {
    id: WorkloadsDashboardItemId.BiggestCostMovers,
    title: 'Biggest cost movers',
    description: 'Workloads with the biggest cost changes',
    x: 4,
    y: 52,
    w: 4,
    h: 4,
    minW: 3,
    minH: 3,
  },
  {
    id: WorkloadsDashboardItemId.ComputeSpend,
    title: 'Compute spend',
    description: 'Compute spend over time with top workloads',
    x: 0,
    y: 56,
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
];

const UTILIZATION_IDS: WorkloadsDashboardItemId[] = [
  WorkloadsDashboardItemId.CpuUtilization,
  WorkloadsDashboardItemId.MemoryUtilization,
];
const IMPACT_IDS: WorkloadsDashboardItemId[] = [
  WorkloadsDashboardItemId.CpuImpact,
  WorkloadsDashboardItemId.MemoryImpact,
];

// Items visible by default (before user saves their own layout) per category.
// Default view: curated Phase-1 set (not every WOOP/category card).
// Attention-needed card is not implemented yet — omitted until available.
export const WORKLOADS_DASHBOARD_DEFAULT_VISIBLE: Record<
  WorkloadCategory,
  WorkloadsDashboardItemId[]
> = {
  [WORKLOAD_CATEGORIES.DEFAULT]: [
    WorkloadsDashboardItemId.ComputeCost,
    WorkloadsDashboardItemId.WorkloadAutoscalerCoverage,
    WorkloadsDashboardItemId.NetworkCost,
    WorkloadsDashboardItemId.CpuUtilization,
    WorkloadsDashboardItemId.MemoryUtilization,
    WorkloadsDashboardItemId.ReliabilityMap,
    WorkloadsDashboardItemId.ComputeSpend,
  ],
  [WORKLOAD_CATEGORIES.AUTOSCALING]: [
    ...UTILIZATION_IDS,
    WorkloadsDashboardItemId.WorkloadAutoscalerCoverage,
    WorkloadsDashboardItemId.RecommendationStatus,
    WorkloadsDashboardItemId.ScalingPolicies,
    WorkloadsDashboardItemId.ReclaimedResources,
    WorkloadsDashboardItemId.SavingsSummary,
  ],
  [WORKLOAD_CATEGORIES.SAVINGS]: [
    ...IMPACT_IDS,
    WorkloadsDashboardItemId.AutoscalerImpact,
    WorkloadsDashboardItemId.ReclaimedResources,
    WorkloadsDashboardItemId.SavingsSummary,
  ],
  [WORKLOAD_CATEGORIES.COMPUTE]: [
    WorkloadsDashboardItemId.ComputeCost,
    WorkloadsDashboardItemId.BiggestCostMovers,
    WorkloadsDashboardItemId.ComputeSpend,
  ],
  [WORKLOAD_CATEGORIES.RELIABILITY]: [
    WorkloadsDashboardItemId.ReliabilityMap,
    WorkloadsDashboardItemId.WorkloadHealth,
    WorkloadsDashboardItemId.WorkloadHealthTrafficShare,
    WorkloadsDashboardItemId.P95LatencyDistribution,
  ],
  [WORKLOAD_CATEGORIES.NETWORK]: [
    WorkloadsDashboardItemId.NetworkCost,
    WorkloadsDashboardItemId.NetworkTraffic,
    WorkloadsDashboardItemId.NetworkCostOverTime,
  ],
  [WORKLOAD_CATEGORIES.GPU_UTILIZATION]: [
    WorkloadsDashboardItemId.GpuWorkloads,
    WorkloadsDashboardItemId.GpuMemoryWaste,
    WorkloadsDashboardItemId.GpuIdleSpend,
  ],
};

/** Primary category per card (folder ownership) for customize-drawer grouping/filter. */
export const WORKLOADS_DASHBOARD_ITEM_CATEGORY: Record<
  WorkloadsDashboardItemId,
  Exclude<WorkloadCategory, typeof WORKLOAD_CATEGORIES.DEFAULT>
> = {
  [WorkloadsDashboardItemId.CpuUtilization]: WORKLOAD_CATEGORIES.AUTOSCALING,
  [WorkloadsDashboardItemId.MemoryUtilization]: WORKLOAD_CATEGORIES.AUTOSCALING,
  [WorkloadsDashboardItemId.WorkloadAutoscalerCoverage]:
    WORKLOAD_CATEGORIES.AUTOSCALING,
  [WorkloadsDashboardItemId.RecommendationStatus]:
    WORKLOAD_CATEGORIES.AUTOSCALING,
  [WorkloadsDashboardItemId.ScalingPolicies]: WORKLOAD_CATEGORIES.AUTOSCALING,
  [WorkloadsDashboardItemId.CpuImpact]: WORKLOAD_CATEGORIES.SAVINGS,
  [WorkloadsDashboardItemId.MemoryImpact]: WORKLOAD_CATEGORIES.SAVINGS,
  [WorkloadsDashboardItemId.AutoscalerImpact]: WORKLOAD_CATEGORIES.SAVINGS,
  [WorkloadsDashboardItemId.ReclaimedResources]: WORKLOAD_CATEGORIES.SAVINGS,
  [WorkloadsDashboardItemId.SavingsSummary]: WORKLOAD_CATEGORIES.SAVINGS,
  [WorkloadsDashboardItemId.NetworkCost]: WORKLOAD_CATEGORIES.NETWORK,
  [WorkloadsDashboardItemId.NetworkTraffic]: WORKLOAD_CATEGORIES.NETWORK,
  [WorkloadsDashboardItemId.NetworkCostOverTime]: WORKLOAD_CATEGORIES.NETWORK,
  [WorkloadsDashboardItemId.GpuWorkloads]: WORKLOAD_CATEGORIES.GPU_UTILIZATION,
  [WorkloadsDashboardItemId.GpuMemoryWaste]:
    WORKLOAD_CATEGORIES.GPU_UTILIZATION,
  [WorkloadsDashboardItemId.GpuIdleSpend]: WORKLOAD_CATEGORIES.GPU_UTILIZATION,
  [WorkloadsDashboardItemId.ReliabilityMap]: WORKLOAD_CATEGORIES.RELIABILITY,
  [WorkloadsDashboardItemId.WorkloadHealth]: WORKLOAD_CATEGORIES.RELIABILITY,
  [WorkloadsDashboardItemId.WorkloadHealthTrafficShare]:
    WORKLOAD_CATEGORIES.RELIABILITY,
  [WorkloadsDashboardItemId.P95LatencyDistribution]:
    WORKLOAD_CATEGORIES.RELIABILITY,
  [WorkloadsDashboardItemId.ComputeCost]: WORKLOAD_CATEGORIES.COMPUTE,
  [WorkloadsDashboardItemId.BiggestCostMovers]: WORKLOAD_CATEGORIES.COMPUTE,
  [WorkloadsDashboardItemId.ComputeSpend]: WORKLOAD_CATEGORIES.COMPUTE,
};

export const getWorkloadsDashboardStorageKey = (category: string) =>
  `workloads-dashboard-${category}-v2`;
